import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GPRace {
  id: string;
  season: number;
  round: number;
  name: string;
  circuit: string | null;
  country: string | null;
  flag: string | null;
  race_date: string;
  sprint_date: string | null;
  status: string;
  winner: string | null;
  sprint_winner: string | null;
  notes: string | null;
}

export function useMotoGPCalendar() {
  const [races, setRaces] = useState<GPRace[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data, error } = await supabase
        .from("motogp_calendar")
        .select("*")
        .eq("season", 2026)
        .order("round", { ascending: true });

      if (!error && data) {
        setRaces(data as unknown as GPRace[]);
      }
      setLoading(false);
    };

    fetch();

    // Listen for realtime updates
    const channel = supabase
      .channel("motogp-calendar-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "motogp_calendar" }, () => {
        fetch();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const nextRace = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return races.find((r) => {
      const raceDate = new Date(r.race_date + "T00:00:00");
      return raceDate >= today && r.status !== "completed" && r.status !== "cancelled";
    }) || null;
  }, [races]);

  const countdown = useMemo(() => {
    if (!nextRace) return { days: 0, hours: 0, label: "Temporada finalizada" };
    const now = new Date();
    const raceDate = new Date(nextRace.race_date + "T14:00:00"); // Race at 2pm approx
    const diff = raceDate.getTime() - now.getTime();
    if (diff <= 0) return { days: 0, hours: 0, label: `¡${nextRace.name} HOY!` };
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    return {
      days,
      hours,
      label: days > 0
        ? `${days}d ${hours}h para ${nextRace.name}`
        : `${hours}h para ${nextRace.name}`,
    };
  }, [nextRace]);

  return { races, nextRace, countdown, loading };
}
